import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronRight, ChevronDown, Eye, Loader2 } from 'lucide-react'
import type { BdrLead } from '@/types'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function getStatusInfo(status: string): { label: string; color: string } {
  const map: Record<string, { label: string; color: string }> = {
    ganho: { label: 'Ganho', color: '#10B981' },
    perdido: { label: 'Perdido', color: '#ef4444' },
    reuniao_agendada: { label: 'Reuniao', color: '#3B82F6' },
    reuniao_realizada: { label: 'Realizada', color: '#A855F7' },
    handoff: { label: 'Handoff', color: '#14BDBD' },
  }
  return map[status] ?? { label: status, color: '#6b7280' }
}

// ─── Month grouping ──────────────────────────────────────────────────────────

type HistoryLead = Pick<BdrLead, 'id' | 'empresa' | 'contato' | 'score' | 'status'> & {
  dataEntrada?: string
  dataAgendada?: string
  reuniaoStatus?: string
  telefone?: string
  createdAt?: string
}

interface MonthGroup {
  key: string
  label: string
  leads: HistoryLead[]
}

function groupByMonth(leads: HistoryLead[]): MonthGroup[] {
  const groups = new Map<string, HistoryLead[]>()

  for (const lead of leads) {
    const dateStr =
      lead.dataAgendada ||
      lead.dataEntrada ||
      lead.createdAt
    if (!dateStr) continue
    const d = new Date(dateStr)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`

    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(lead)
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, leads]) => ({
      key,
      label: leads[0]
        ? new Date(
            leads[0].dataAgendada ||
              leads[0].dataEntrada ||
              leads[0].createdAt ||
              '',
          ).toLocaleDateString('pt-BR', {
            month: 'long',
            year: 'numeric',
          })
        : key,
      leads,
    }))
    .map((g) => ({ ...g, label: g.label.charAt(0).toUpperCase() + g.label.slice(1) }))
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface BdrHistoryTableProps {
  historyData: HistoryLead[]
  loading: boolean
  openLeadDetail: (lead: BdrLead) => void
}

// ─── Component ───────────────────────────────────────────────────────────────

export function BdrHistoryTable({ historyData, loading, openLeadDetail }: BdrHistoryTableProps) {
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set())

  const monthGroups = useMemo(() => groupByMonth(historyData), [historyData])

  const toggleMonth = (key: string) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (loading) {
    return (
      <div className='glass-card border-white/[0.06] flex items-center justify-center py-16'>
        <Loader2 size={24} className='animate-spin text-accent' />
      </div>
    )
  }

  if (monthGroups.length === 0) {
    return (
      <div className='glass-card border-white/[0.06] text-center py-16 text-text-muted'>
        Nenhuma reunião realizada encontrada.
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className='space-y-2'
    >
      {monthGroups.map((group) => {
        const isExpanded = expandedMonths.has(group.key)

        return (
          <div key={group.key} className='glass-card border-white/[0.06] overflow-hidden'>
            {/* Month header */}
            <button
              onClick={() => toggleMonth(group.key)}
              className='flex items-center gap-2.5 w-full px-4 py-3 text-left hover:bg-white/[0.02] transition-colors min-h-[44px]'
            >
              {isExpanded ? (
                <ChevronDown size={14} className='text-text-muted' />
              ) : (
                <ChevronRight size={14} className='text-text-muted' />
              )}
              <span className='text-text-primary text-sm font-semibold flex-1 capitalize'>
                {group.label}
              </span>
              <span className='text-[11px] font-bold px-2 py-0.5 rounded-full text-accent bg-accent/10'>
                {group.leads.length}
              </span>
            </button>

            {/* Expanded table */}
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className='overflow-hidden'
                >
                  <div className='overflow-x-auto border-t border-white/[0.04]'>
                    <table className='w-full text-xs'>
                      <thead>
                        <tr className='border-b border-white/[0.04]'>
                          <th className='text-left px-3 py-2 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                            Empresa
                          </th>
                          <th className='text-left px-3 py-2 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                            Contato
                          </th>
                          <th className='text-left px-3 py-2 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                            Data reunião
                          </th>
                          <th className='text-left px-3 py-2 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                            Score
                          </th>
                          <th className='text-left px-3 py-2 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                            Status
                          </th>
                          <th className='text-right px-3 py-2 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                            Acoes
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.leads.map((lead) => {
                          const status = getStatusInfo(lead.status)
                          const score = lead.score ?? 0

                          return (
                            <tr
                              key={lead.id}
                              onClick={() => openLeadDetail(lead as BdrLead)}
                              className='border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer transition-colors'
                            >
                              {/* Empresa */}
                              <td className='px-3 py-2'>
                                <div className='flex items-center gap-2'>
                                  <div className='w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold text-[10px] shrink-0'>
                                    {getInitials(lead.empresa)}
                                  </div>
                                  <span className='text-text-primary font-medium truncate'>
                                    {lead.empresa}
                                  </span>
                                </div>
                              </td>

                              {/* Contato */}
                              <td className='px-3 py-2 text-text-muted truncate'>{lead.contato}</td>

                              {/* Data */}
                              <td className='px-3 py-2 text-text-muted whitespace-nowrap'>
                                {formatDate(
                                  (lead as BdrLead & { dataAgendada?: string }).dataAgendada ||
                                    lead.dataEntrada,
                                )}
                              </td>

                              {/* Score */}
                              <td className='px-3 py-2'>
                                <span className='text-text-primary font-medium'>{score}%</span>
                              </td>

                              {/* Status */}
                              <td className='px-3 py-2'>
                                <span
                                  className='text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider'
                                  style={{
                                    color: status.color,
                                    backgroundColor: `${status.color}18`,
                                  }}
                                >
                                  {status.label}
                                </span>
                              </td>

                              {/* Acoes */}
                              <td className='px-3 py-2'>
                                <div className='flex items-center justify-end'>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openLeadDetail(lead as BdrLead)
                                    }}
                                    title='Ver detalhes'
                                    className='min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-white/[0.06] transition-colors'
                                  >
                                    <Eye size={15} className='text-text-muted' />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </motion.div>
  )
}
